import * as http from "./http";
import {
  ApiResponse,
  Company,
  Customer,
  PaginatedApiResponse,
  ListSort,
} from "../models";
import ApiService from "./service";
import {
  AddNewCompanyDocument,
  CompanyOffice,
  Dict,
  Lead,
  Proposal,
  RequirementResponseData,
  UpdateCompanyData,
} from "../..";
import { Invoice } from "../models/configuration";

export class CompanyService extends ApiService {
  public async searchCompany(query: string): Promise<Company[]> {
    const url = `${this.apiDomain}/api/company/search?query=${query}`;

    const response = await http.get<ApiResponse<Company[]>>(url, this.store);

    return response?.data?.data;
  }

  public async createCompany(data: Company) {
    const url = `${this.apiDomain}/api/company/`;

    const response = await http.post<ApiResponse<Company>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async createCustomer(data: Customer) {
    const url = `${this.apiDomain}/api/customer/corporate`;

    const response = await http.post<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async searchCustomer(
    query: string,
    companyId: string
  ): Promise<Customer[]> {
    const url = `${this.apiDomain}/api/customer/search?query=${query}&companyId=${companyId}`;

    const response = await http.get<PaginatedApiResponse<Customer>>(
      url,
      this.store
    );

    return response?.data?.data;
  }

  public async updateCompany(id: string, data: Company) {
    const url = `${this.apiDomain}/api/company/${id}`;

    const response = await http.put<ApiResponse<Company>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async updateSocialMediaDetails(data: Customer, id?: string) {
    const url = `${this.apiDomain}/api/company/${id}/social-media-details`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      data,
      this.store
    );

    return response?.data;
  }

  public async getCustomersByAccess(
    data: {
      isDeal?: boolean;
      customerId?: string;
    } & ListSort &
      Dict
  ): Promise<ApiResponse<{ customer: Customer; lastAccessedDate: Date }[]>> {
    const url = `${this.apiDomain}/api/company/by-access`;

    const resposne = await http.post<
      ApiResponse<{ customer: Customer; lastAccessedDate: Date }[]>
    >(url, data, this.store);
    return resposne.data;
  }

  // get company list
  public async getCompanyList(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<Company> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/company/list`;

    const resposne = await http.post<PaginatedApiResponse<Company>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  // get company user list
  public async getCompanyUsersList(
    data: {
      page: number;
      perPage: number;
    },
    id?: string,
  ): Promise<PaginatedApiResponse<Customer> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/lead-360/company/${id}/users`;

    const resposne = await http.post<PaginatedApiResponse<Customer>>(
      url,
      data,
      this.store
    );
    return { ...resposne?.data, lastUpdatedAt: new Date() };
  }

    // get company requirements  list
    public async getCompanyRequirementsList(
      data: {
        page: number;
        perPage: number;
      },
      id?: string,
    ): Promise<PaginatedApiResponse<RequirementResponseData> & { lastUpdatedAt: Date }> {
      const url = `${this.apiDomain}/api/lead-360/company/${id}/requirements`;
  
      const resposne = await http.post<PaginatedApiResponse<RequirementResponseData>>(
        url,
        data,
        this.store
      );
      return { ...resposne?.data, lastUpdatedAt: new Date() };
    }

    // get company proposals  list
    public async getCompanyProposalsList(
      data: {
        page: number;
        perPage: number;
      },
      id?: string,
    ): Promise<PaginatedApiResponse<Proposal> & { lastUpdatedAt: Date }> {
      const url = `${this.apiDomain}/api/lead-360/company/${id}/proposals`;
  
      const resposne = await http.post<PaginatedApiResponse<Proposal>>(
        url,
        data,
        this.store
      );
      return { ...resposne?.data, lastUpdatedAt: new Date() };
    }

    // get company user list
    public async getCompanyLeadsList(
      data: {
        page: number;
        perPage: number;
      },
      id?: string,
    ): Promise<PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }> {
      const url = `${this.apiDomain}/api/lead-360/company/${id}/leads`;
  
      const resposne = await http.post<PaginatedApiResponse<Lead>>(
        url,
        data,
        this.store
      );
      return { ...resposne?.data, lastUpdatedAt: new Date() };
    }

  public async updateCompnay(data: UpdateCompanyData, id?: string) {
    const url = `${this.apiDomain}/api/company/${id}`;

    const response = await http.put<ApiResponse<Company>>(
      url,
      data,
      this.store
    );

    return response?.data?.data;
  }

  public async getDetails(id: string): Promise<Company> {
    const url = `${this.apiDomain}/api/company/${id}`;
    const response = await http.get<ApiResponse<Company>>(url, this.store);

    return response?.data?.data;
  }

  public async getCompaniesById(companyIds: (string | undefined)[]) {
    if (companyIds) {
      const data = { companyIds };
      const url = `${this.apiDomain}/api/company/companies`;
      const response = await http.post<ApiResponse<Company[]>>(
        url,
        data,
        this.store
      );
      return response.data.data;
    } else {
      return;
    }
  }

  public async getCompanyOfficesById(companyId: string): Promise<Customer[]> {
    const url = `${this.apiDomain}/api/company-office/company/${companyId}`;
    const response = await http.get<PaginatedApiResponse<any>>(url, this.store);
    return response?.data?.data;
  }

  public async getGroupCompanyiesById(companyId: string): Promise<Customer[]> {
    const url = `${this.apiDomain}/api/company/group-company/${companyId}`;
    const response = await http.get<PaginatedApiResponse<any>>(url, this.store);
    return response?.data?.data;
  }

  public async addNewCompanyDocument(data: AddNewCompanyDocument) {
    const url = `${this.apiDomain}/api/legal-due-diligence/company-document`;

    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data;
  }

  public async mergeCompany(
    id: string,
    mergingCompanyIds : string[],

    data: Customer
  ) {
    const url = `${this.apiDomain}/api/company/${id}/merge-companies`;

    const response = await http.put<ApiResponse<Customer>>(
      url,
      { mergingCompanyIds , ...data },
      this.store
    );

    return response;
  }

  public async listByCompany(companyId: string): Promise<any> {
    const url = `${this.apiDomain}/api/legal-due-diligence/company-document/${companyId}`;

    const resposne = await http.get<ApiResponse<any>>(url, this.store);
    return resposne.data?.data || [];
  }

  public async uploadCompanyDocument(data: any, documentId: string) {
    const url = `${this.apiDomain}/api/legal-due-diligence/company-document/upload/${documentId}`;
    const response = await http.post<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  public async getCompanyPeoples(companyId: string): Promise<any> {
    const url = `${this.apiDomain}/api/company/peoples/${companyId}`;
    const response = await http.get<ApiResponse<any>>(url, this.store);
    return response?.data?.data;
  }

  public async requestCompanyDocument(data: any, id: string) {
    const url = `${this.apiDomain}/api/legal-due-diligence/company-document/request/${id}`;

    const response = await http.put<ApiResponse<any>>(url, data, this.store);
    return response.data?.data;
  }

  // download companylist
  public async downloadCompanyList(
    data: {
      search?: string;
      columns: { label: string; source?: string }[];
      // isDeal?: boolean;
    } & ListSort &
      Dict
  ): Promise<void> {
    const url = `${this.apiDomain}/api/company/download`;

    const resposne = await http.downloadPostData(url, data, this.store);
    http.saveDownloadedFile(
      resposne.data,
      "application/application/vnd.openxmlformats",
      `company-${new Date()}.xlsx`
    );
  }

  public async getCompanyOfficeList(
    data: {
      page: number;
      search?: string;
      perPage: number;
    } & ListSort &
      Dict
  ): Promise<PaginatedApiResponse<CompanyOffice> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/company-office/list`;

    const resposne = await http.post<PaginatedApiResponse<CompanyOffice>>(
      url,
      data,
      this.store
    );
    return { ...resposne.data, lastUpdatedAt: new Date() };
  }

  public async searchCompanyInline(data: {
    query?: string;
  }): Promise<ApiResponse<any>> {
    const url = `${this.apiDomain}/api/company/search-inline`;
    const response = await http.post<PaginatedApiResponse<any>>(
      url,
      data,
      this.store
    );
    return response?.data;
  }

  // get company invoices list
  public async getCompanyInvoicesList(
    data: {
      page: number;
      perPage: number;
    },
    id?: string,
  ): Promise<PaginatedApiResponse<Invoice> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/lead-360/company/${id}/invoices`;

    const response = await http.post<PaginatedApiResponse<Invoice>>(
      url,
      data,
      this.store
    );
    return { ...response?.data, lastUpdatedAt: new Date() };
  }

  // get company agreements list
  public async getCompanyAgreementsList(
    data: {
      page: number;
      perPage: number;
    },
    id?: string,
  ): Promise<PaginatedApiResponse<any> & { lastUpdatedAt: Date }> {
    const url = `${this.apiDomain}/api/lead-360/company/${id}/agreements`;

    const response = await http.post<PaginatedApiResponse<any>>(
      url,
      data,
      this.store
    );
    return { ...response?.data, lastUpdatedAt: new Date() };
  }
}
